import { GENERIC_IDP_ID, GuideType, Method, SCIMMethod } from 'consts';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { IIdPConfigContextValue } from 'types/context';

const IdPConfigContext = createContext<IIdPConfigContextValue | undefined>(
	undefined
);

interface IIdPConfigProviderProps {
	children: React.ReactNode;
	initGuideType?: GuideType | null;
	initIdpID?: string | null;
	initMethod?: Method | null;
	initGuideStepNum?: number | null;
	initIsSubTenant?: boolean;
}

export const IdPConfigContextProvider: React.FC<IIdPConfigProviderProps> = ({
	children,
	initGuideType = null,
	initIdpID = null,
	initMethod = null,
	initGuideStepNum = null,
	initIsSubTenant = false
}) => {
	const [guideType, setGuideType] = useState<GuideType | null>(initGuideType);
	const [idpID, setIdPID] = useState<string | null>(initIdpID);
	const [method, setMethod] = useState<Method | null>(initMethod);
	const [guideStepNum, setGuideStepNum] = useState<number | null>(
		initGuideStepNum
	);

	const [scimIdpID, setScimIdPID] = useState<string | null>(initIdpID);
	const [scimMethod, setScimMethod] = useState<SCIMMethod | null>(
		initMethod as any as SCIMMethod
	);
	const [scimGuideStepNum, setScimGuideStepNum] = useState<number | null>(
		initGuideStepNum
	);
	const [xaaIdpID, setXaaIdPID] = useState<string | null>(null);
	const [xaaMethod, setXaaMethod] = useState<Method | null>(null);
	const [xaaGuideStepNum, setXaaGuideStepNum] = useState<number | null>(
		initGuideStepNum
	);
	const [isSubTenant, setIsSubTenant] = useState<boolean>(initIsSubTenant);

	const value = useMemo(
		() => ({
			guideType,
			setGuideType,
			idpID,
			setIdPID,
			method,
			setMethod,
			guideStepNum,
			setGuideStepNum,
			scimIdpID,
			setScimIdPID,
			scimMethod,
			setScimMethod,
			scimGuideStepNum,
			setScimGuideStepNum,
			xaaIdpID,
			setXaaIdPID,
			xaaMethod,
			setXaaMethod,
			xaaGuideStepNum,
			setXaaGuideStepNum,
			isSubTenant,
			setIsSubTenant
		}),
		[
			idpID,
			method,
			guideStepNum,
			scimIdpID,
			setScimIdPID,
			scimMethod,
			setScimMethod,
			scimGuideStepNum,
			setScimGuideStepNum,
			xaaIdpID,
			setXaaIdPID,
			xaaMethod,
			setXaaMethod,
			xaaGuideStepNum,
			setXaaGuideStepNum,
			guideType,
			setGuideType,
			isSubTenant
		]
	);

	return (
		<IdPConfigContext.Provider value={value}>
			{children}
		</IdPConfigContext.Provider>
	);
};

export const useIdPConfigContext = (): IIdPConfigContextValue => {
	const context = useContext(IdPConfigContext);
	if (!context) throw new Error('IdPConfigContextProvider is missing');

	return context;
};

export const useCurrentMethod = () => {
	const { method, scimMethod, guideType } = useIdPConfigContext();
	return useMemo(() => {
		if (guideType === GuideType.XAA) {
			return Method.Xaa;
		}
		if (guideType === GuideType.SSO) {
			return method || Method.Saml;
		}
		switch (scimMethod) {
			case SCIMMethod.Oidc:
				return Method.Oidc;
			case SCIMMethod.Generic:
			default:
				return Method.Saml;
		}
	}, [method, scimMethod, guideType]);
};

/**
 * The IdP id of the current guide — the SCIM IdP when rendered inside a SCIM
 * guide, the XAA IdP inside an XAA guide (falling back to the generic content
 * idp), the SSO IdP otherwise. Shared fields (like the attribute-mapping step)
 * must use this, never the raw SSO idpID — otherwise they read null in the XAA
 * (or SCIM) context and never initialize.
 */
export const useCurrentGuideIdPID = (): string | null => {
	const { idpID, scimIdpID, xaaIdpID, guideType } = useIdPConfigContext();
	return useMemo(() => {
		if (guideType === GuideType.SCIM) {
			return scimIdpID;
		}
		if (guideType === GuideType.XAA) {
			return xaaIdpID || GENERIC_IDP_ID;
		}
		return idpID;
	}, [idpID, scimIdpID, xaaIdpID, guideType]);
};
